/**
 * Workout history. Reads from Dexie, so the full history is browsable offline.
 */

import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { db, type SetRow } from '../db'
import { getSessionSets, summarizeSessions, type SessionSummary } from '../db/queries'
import { deleteSession } from '../db/mutations'
import { syncSoon } from '../sync/engine'
import { defaultDistanceUnit, type DistancePref, type Unit } from '../lib/units'
import { summaryLine } from '../lib/sessionSummary'
import { formatSet } from '../lib/setFormat'
import { formatRound, groupRounds } from '../lib/rounds'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { parseIso } from '../lib/time'

export function HistoryScreen({
  unit,
  distancePref,
}: {
  unit: Unit
  distancePref: DistancePref
}) {
  const summaries = useLiveQuery(() => summarizeSessions(60), [], [] as SessionSummary[])
  const [openId, setOpenId] = useState<string | null>(null)

  if (summaries.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          Nothing logged yet. Finished workouts show up here.
        </p>
      </div>
    )
  }

  return (
    <>
      <h1>History</h1>
      <ul className="plain">
        {summaries.map((summary) => {
          const { session, setCount, exercises, distanceScale } = summary
          // The account default for the scale, not a per-exercise override:
          // this line covers a whole session, and an override belongs to one
          // exercise. `long` when there is no distance at all, which is unused
          // — summaryLine only asks for a unit when there is something to put
          // in it.
          const du = defaultDistanceUnit(distanceScale ?? 'long', distancePref, unit)
          return (
          <li key={session.id} style={{ display: 'block' }}>
            <div
              className="row tappable"
              onClick={() => setOpenId(openId === session.id ? null : session.id)}
            >
              <div>
                <strong>{session.title || 'Workout'}</strong>
                <div className="muted">
                  {formatDate(session.started_at)}
                  {!session.finished_at && ' · in progress'}
                </div>
              </div>
              <div className="muted" style={{ textAlign: 'right' }}>
                <div>{setCount} set{setCount === 1 ? '' : 's'}</div>
                <div>{summaryLine(summary, { unit, du })}</div>
              </div>
            </div>

            {exercises.length > 0 && (
              <div className="muted" style={{ marginTop: '.25rem', fontSize: '.85rem' }}>
                {exercises.join(' · ')}
              </div>
            )}

            {openId === session.id && (
              <SessionDetail distancePref={distancePref} sessionId={session.id} unit={unit} />
            )}
          </li>
          )
        })}
      </ul>
    </>
  )
}

function SessionDetail({
  sessionId,
  unit,
  distancePref,
}: {
  sessionId: string
  unit: Unit
  distancePref: DistancePref
}) {
  const sets = useLiveQuery(() => getSessionSets(sessionId), [sessionId], [] as SetRow[])
  const exercises = useLiveQuery(() => db.exercises.toArray(), [], [])
  const byId = new Map(exercises.map((e) => [e.id, e]))
  const overrides = useDistanceOverrides()
  const [confirming, setConfirming] = useState(false)

  const grouped = new Map<number, SetRow[]>()
  for (const s of sets) {
    if (!grouped.has(s.exercise_id)) grouped.set(s.exercise_id, [])
    grouped.get(s.exercise_id)!.push(s)
  }

  return (
    <div style={{ margin: '.6rem 0 .3rem', paddingLeft: '.5rem',
                  borderLeft: '2px solid var(--border)' }}>
      {[...grouped.entries()].map(([exerciseId, group]) => (
        <div key={exerciseId} style={{ marginBottom: '.5rem' }}>
          <div style={{ fontSize: '.92rem' }}>
            {byId.get(exerciseId)?.name ?? 'Unknown'}
          </div>
          <div className="muted" style={{ fontSize: '.85rem' }}>
            {groupRounds(group)
              .map((round) => formatRound(round, (s) => formatSet(s, byId.get(exerciseId), unit, {
                du: unitFor(byId.get(exerciseId),
                            { unit, pref: distancePref, overrides }),
              })))
              .join('  ·  ')}
          </div>
          {/* Notes are why you'd open a session from three weeks ago. */}
          {group.filter((s) => s.notes).map((s) => (
            <div key={s.id} className="muted"
                 style={{ fontSize: '.8rem', fontStyle: 'italic' }}>
              {s.notes}
            </div>
          ))}
        </div>
      ))}

      {confirming ? (
        <div className="row" style={{ marginTop: '.5rem' }}>
          <span className="muted">Delete this workout?</span>
          <span>
            <button
              className="secondary"
              style={{ marginTop: 0, padding: '.2rem .6rem', minHeight: 0, fontSize: '.8rem' }}
              onClick={() => setConfirming(false)}
            >
              No
            </button>
            <button
              style={{ marginTop: 0, marginLeft: '.4rem', padding: '.2rem .6rem',
                       minHeight: 0, fontSize: '.8rem',
                       background: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={async () => {
                await deleteSession(sessionId)
                syncSoon(0)
              }}
            >
              Delete
            </button>
          </span>
        </div>
      ) : (
        <button
          className="secondary"
          style={{ marginTop: '.4rem', padding: '.2rem .6rem', minHeight: 0, fontSize: '.8rem' }}
          onClick={() => setConfirming(true)}
        >
          Delete workout
        </button>
      )}
    </div>
  )
}

function formatDate(iso: string): string {
  const d = parseIso(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (days === 0) return `Today, ${time}`
  if (days === 1) return `Yesterday, ${time}`
  return d.toLocaleDateString([], {
    weekday: 'short', month: 'short', day: 'numeric',
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  })
}
